/**
 * Bloc de liens internes SEO vers les pages "location voiture [ville]"
 * Utilisé dans Footer, Blog, pages ville pour maillage interne massif.
 */

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";
import {
  MAIN_CITIES,
  getCityInternalLinkPath,
  getCityLinkAnchor,
  type CityInternalLink,
  type InternalLinkLocale,
} from "@/lib/data/internal-links-cities";
import { getLocalizedCityName } from "@/lib/i18n/cityNames";

const toInternalLinkLocale = (language: string): InternalLinkLocale => {
  if (language.startsWith("ar")) return "ar";
  if (language.startsWith("en")) return "en";
  return "fr";
};

const linkClass = "text-green-night hover:text-green font-semibold underline decoration-2 underline-offset-2 transition-colors";

interface CityLinksBlockProps {
  useSeoAnchor?: boolean;
  variant?: "inline" | "list" | "footer";
  cities?: CityInternalLink[];
  className?: string;
  title?: string;
}

export const CityLinksBlock = ({
  useSeoAnchor = true,
  variant = "inline",
  cities = MAIN_CITIES,
  className = "",
  title,
}: CityLinksBlockProps) => {
  const { t, i18n } = useTranslation();
  const linkLocale = toInternalLinkLocale(i18n.language);

  const getLabel = (city: CityInternalLink) => {
    const localizedName = getLocalizedCityName(city.name, t);
    if (!useSeoAnchor) return localizedName;
    if (linkLocale !== "fr") return getCityLinkAnchor(city, linkLocale);
    return t("cityPages.otherCities.linkTitle", { city: localizedName });
  };

  if (variant === "footer") {
    return (
      <nav aria-label={title || t("blog.cityLinksTitle")} className={className}>
        {title && (
          <h3 className="font-bold text-xs sm:text-sm uppercase tracking-[0.1em] mb-4 sm:mb-5 text-[#10b981]">
            {title}
          </h3>
        )}
        <ul className="space-y-2 sm:space-y-3">
          {cities.map((city) => (
            <li key={city.slug}>
              <Link
                to={getCityInternalLinkPath(city.slug, linkLocale)}
                className="text-sm sm:text-base text-white hover:text-[#10b981] font-medium transition-colors duration-200 inline-flex items-center gap-2 group"
              >
                <MapPin className="w-3.5 h-3.5 text-white group-hover:text-[#10b981] transition-colors flex-shrink-0" aria-hidden />
                <span className="text-white">{getLabel(city)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    );
  }

  if (variant === "list") {
    return (
      <div className={className}>
        {title && (
          <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">{title}</h3>
        )}
        <ul className="flex flex-wrap gap-x-4 gap-y-2">
          {cities.map((city) => (
            <li key={city.slug}>
              <Link to={getCityInternalLinkPath(city.slug, linkLocale)} className={linkClass}>
                {getLabel(city)}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className={className}>
      {title && (
        <span className="text-text-secondary text-sm font-medium mr-2">{title}</span>
      )}
      {cities.map((city, index) => (
        <span key={city.slug}>
          {index > 0 && <span className="text-text-secondary mx-1">•</span>}
          <Link to={getCityInternalLinkPath(city.slug, linkLocale)} className={linkClass}>
            {getLabel(city)}
          </Link>
        </span>
      ))}
    </div>
  );
};

export default CityLinksBlock;
